import { CredentialsError, BackendError, TooManyRequestsError } from '../errors/api_errors';
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from '@/core/http-methods';
import { IhttpResponse } from '@/interfaces/http-interfaces';
import {
  UsersDepartments,
  FullBot,
  FullUser,
  PostUserData,
  PostBotData,
  DepartmentInfo,
  UserSettings,
} from '@/domain/user';
import globalStore from '../store/globalStore';

export type userLoginSuccess = {
  user_id: number;
  token: string;
};

export const loginApi = async (
  login: string,
  password: string
): Promise<userLoginSuccess> => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/login`;
  const body = {
    username: login,
    password: password,
  };
  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body.data as userLoginSuccess;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  if (res.status === 429) {
    throw new TooManyRequestsError('Слишком много попыток входа. Попробуйте позже');
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const getUsersDepartments = async (): Promise<UsersDepartments> => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/`;
  const res: IhttpResponse = await getRequest(url);
  if (res.status === 200) {
    const newUserDepartments = res.body as UsersDepartments;
    await globalStore.dispatch('changeUserDepartments', newUserDepartments);
    return newUserDepartments;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const getBotInfoById = async (id: number) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/${id}`;
  const res: IhttpResponse = await getRequest(url);

  if (res.status === 200) {
    const botInfo = res.body;
    return botInfo as FullBot;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
};

export const getUserInfoById = async (id: number) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/${id}`;
  const res: IhttpResponse = await getRequest(url);

  if (res.status === 200) {
    const user = res.body as FullUser;
    return user;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
};

export const updateUserAvatar = async (
  user_id: number,
  newAvatar: FormData
) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/${user_id}/avatar`;

  const headers = { 'Content-Type': 'multipart/form-data' };
  const res: IhttpResponse = await postRequest(url, newAvatar, headers);

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const updateBotAvatar = async (bot_id: number, newAvatar: FormData) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/bots/${bot_id}/avatar`;

  const headers = { 'Content-Type': 'multipart/form-data' };
  const res: IhttpResponse = await postRequest(url, newAvatar, headers);

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const updateUserById = async (
  user_id: number,
  postUserData: PostUserData
) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/${user_id}`;

  const res: IhttpResponse = await putRequest(url, postUserData);

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const updateBotById = async (
  user_id: number,
  postUserData: PostBotData
) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/bots/${user_id}`;

  const res: IhttpResponse = await putRequest(url, postUserData);

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

// отделы
export const addUserToDepartment = async (
  user_id: number,
  department_id: number
) => {
  const url = `${
    import.meta.env.VITE_RADAR_URL
  }/departments/${department_id}/users/${user_id}`;

  const res: IhttpResponse = await postRequest(url, {});

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const deleteUserFromDepartment = async (
  user_id: number,
  department_id: number
) => {
  const url = `${
    import.meta.env.VITE_RADAR_URL
  }/departments/${department_id}/users/${user_id}`;

  const res: IhttpResponse = await deleteRequest(url);

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    console.debug(res.body.message);
    throw new CredentialsError(res.body.message);
  }
  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const getDepartmentInfoById = async (
  departmentId: number
): Promise<DepartmentInfo> => {
  const url = `${import.meta.env.VITE_RADAR_URL}/departments/${departmentId}`;

  const res: IhttpResponse = await getRequest(url);

  if (res.status === 200) {
    return res.body as DepartmentInfo;
  }
  if (res.status === 401) {
    throw new CredentialsError(res.body.message);
  }

  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const updateDepartmentName = async (
  departmentId: number,
  name: string
) => {
  const url = `${import.meta.env.VITE_RADAR_URL}/departments/${departmentId}`;

  const body = {
    id: departmentId,
    name,
  };

  const res: IhttpResponse = await putRequest(url, body);

  if (res.status === 200) {
    return res.body as DepartmentInfo;
  }
  if (res.status === 401) {
    throw new CredentialsError(res.body.message);
  }

  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const updateDepartmentAvatar = async (
  departmentId: number,
  file: FormData
) => {
  const url = `${
    import.meta.env.VITE_RADAR_URL
  }/departments/${departmentId}/avatar`;

  const headers = { 'Content-Type': 'multipart/form-data' };
  const res: IhttpResponse = await postRequest(url, file, headers);

  if (res.status === 200) {
    return res.body;
  }
  if (res.status === 401) {
    throw new CredentialsError(res.body.message);
  }

  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const getUserSettings = async () => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/settings`;

  const res: IhttpResponse = await getRequest(url);

  if (res.status === 200) {
    return res.body as UserSettings;
  }
  if (res.status === 401) {
    throw new CredentialsError(res.body.message);
  }

  throw new BackendError({ message: JSON.stringify(res.body) });
};

export const updateUserSettings = async (
  setting: UserSettings
): Promise<UserSettings> => {
  const url = `${import.meta.env.VITE_RADAR_URL}/users/settings`;

  const res: IhttpResponse = await putRequest(url, setting);

  if (res.status === 200) {
    return res.body as UserSettings;
  }
  if (res.status === 401) {
    throw new CredentialsError(res.body.message);
  }

  throw new BackendError({ message: JSON.stringify(res.body) });
};
